import React, { FC } from 'react';

import { Resources } from '@ariksa/inventory-core/api';
import { Box, HStack, Stack, Text } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { colorOpacity, customTheme } from 'theme';

import {
  ConfirmationInput,
  DetailsIdentifier,
  DottedUnderlineLink,
  EllipsisText,
  PageHeaderWithIcon,
  WarningAlert,
} from 'components/DataDisplay';
import { CancelButton, PrimaryButton } from 'components/DataEntry';
import { SearchIcon } from 'components/Icons';
import { Modal } from 'components/Overlay';
import { useResourceType } from 'containers/App/hooks/useResourceType';
import { useSearchParamFunctions } from 'containers/App/hooks/useSearchParamFunctions';
import { renderVulnerabilities } from 'containers/Inventory/CloudInventory/Components/utils';
import { useCloudAccountId } from 'containers/Setup/CloudAccounts/utils';
import { selectSharedState } from 'containers/SharedState/selectors';
import { actions as sharedStateActions } from 'containers/SharedState/slice';
import {
  CONTAINER_ON_VIRTUAL_MACHINE,
  VulnerabilityViewByTabs,
} from 'containers/Visibility/Vulnerabilities/types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  currentResource: any;
  vulnerabilities: Record<string, any>;
  resourceType: string;
}

export const VulnerabilityScanConfirmation: FC<Props> = props => {
  const {
    isOpen,
    onClose,
    currentResource,
    vulnerabilities,
    resourceType,
  } = props;
  const dispatch = useDispatch();
  const [command, setCommand] = React.useState('');
  const { renderAccountWithVPCAndRegion } = useCloudAccountId();
  const { getCloudNativeName } = useResourceType();
  const { instanceScan } = useSelector(selectSharedState);
  const { updateValuesInURL } = useSearchParamFunctions();

  const redirectToVM = () => {
    updateValuesInURL({
      tab: Resources.VirtualMachine,
      search_term:
        currentResource?.deployed_in?.ec2 ??
        currentResource?.deployed_in?.ec2_id,
    });
    /*dispatch(
      sharedStateActions.updateFinalSearchTerm(
        currentResource?.deployed_in?.ec2 ??
          currentResource?.deployed_in?.ec2_id,
      ),
    );*/
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      header={
        <PageHeaderWithIcon
          label="Perform Vulnerability Scan"
          icon={<SearchIcon />}
          iconColor={customTheme.colors.primary}
          iconBgColor={colorOpacity(customTheme.colors.primary, 0.2)}
          useCustomColor
          border={false}
        />
      }
      body={
        <Stack spacing={4}>
          <WarningAlert
            message={
              resourceType === CONTAINER_ON_VIRTUAL_MACHINE
                ? 'Requires an active Outpost for Vulnerability in the region of this ' +
                  'resource. To scan a container, scan the virtual machine on which ' +
                  'this container is hosted.  '
                : 'Requires an active Outpost for Vulnerability in the region ' +
                  'of this resource. This resource will be scanned after any currently ' +
                  'scanned or previously queued resources'
            }
            align="center"
          />
          {resourceType === CONTAINER_ON_VIRTUAL_MACHINE ? (
            <Box pt={2}>
              <DetailsIdentifier label="Type" value="Container" />
            </Box>
          ) : (
            <>
              <Box pt={2}>
                <DetailsIdentifier
                  label="Resource"
                  value={currentResource?.name}
                />
              </Box>
              <DetailsIdentifier
                label="Cloud ID"
                value={currentResource?.resource_id}
              />
            </>
          )}

          <DetailsIdentifier
            label="Deployed In"
            value={renderAccountWithVPCAndRegion(currentResource)}
          />
          {resourceType !== CONTAINER_ON_VIRTUAL_MACHINE && (
            <DetailsIdentifier
              label="Type"
              value={getCloudNativeName(currentResource?.native_name)}
            />
          )}
          <DetailsIdentifier
            label="Vulnerabilities"
            value={renderVulnerabilities({ value: vulnerabilities })}
          />
          {resourceType === CONTAINER_ON_VIRTUAL_MACHINE ? (
            <>
              <DetailsIdentifier
                label="Virtual Machine to Scan"
                value={
                  <HStack>
                    <EllipsisText flex={1}>
                      {currentResource?.deployed_in?.ec2 ??
                        currentResource?.deployed_in?.ec2_id}
                    </EllipsisText>
                    <Box whiteSpace="nowrap">
                      <DottedUnderlineLink
                        label="Take me there"
                        onClick={redirectToVM}
                      />
                    </Box>
                  </HStack>
                }
              />
              <DetailsIdentifier
                label="Cloud ID"
                value={
                  <EllipsisText>{currentResource?.resource_id}</EllipsisText>
                }
              />
            </>
          ) : (
            <ConfirmationInput
              confirmationText="CONFIRM"
              label={
                <Text color={customTheme.colors.gray['250']}>
                  Type CONFIRM below to scan
                </Text>
              }
              command={command}
              setCommand={setCommand}
              paddingTop={0}
            />
          )}
        </Stack>
      }
      footer={
        <HStack justify="space-between" w="full">
          <CancelButton onClick={() => onClose()}>CANCEL</CancelButton>
          <PrimaryButton
            onClick={() => {
              if (resourceType === CONTAINER_ON_VIRTUAL_MACHINE) {
                redirectToVM();
              } else
                dispatch(
                  sharedStateActions.triggerInstanceScan({
                    q: {
                      instanceId: currentResource?.resource_id,
                      resourceUUID:
                        currentResource?.uuid ?? currentResource?.resource_uuid,
                      accountId: currentResource?.account_id,
                      region: currentResource?.region,
                      resourceType:
                        currentResource?.resource_type ?? resourceType,
                    },
                    onSuccess: () => onClose(),
                  }),
                );
            }}
            isDisabled={
              resourceType === CONTAINER_ON_VIRTUAL_MACHINE
                ? false
                : command !== 'CONFIRM'
            }
            px={6}
            isLoading={instanceScan.isLoading}
          >
            OK
          </PrimaryButton>
        </HStack>
      }
      styles={{ modal: { size: '2xl' } }}
    />
  );
};
